import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Users, Clock, Award, Plus, Check, X, Filter, Download, Calendar, ChevronDown, ChevronUp, Trophy, Star } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const ACTIVITY_TYPES = [
  { value: 'ground-setup', label: '🏟️ Ground Setup', description: 'Setting up boundary, stumps, pitch preparation' },
  { value: 'umpiring', label: '⚖️ Umpiring', description: 'Officiating matches' },
  { value: 'scoring', label: '📊 Scoring', description: 'Keeping score during matches' },
  { value: 'equipment', label: '🎒 Equipment Management', description: 'Managing, transporting, maintaining equipment' },
  { value: 'transport', label: '🚗 Transport', description: 'Driving players or equipment' },
  { value: 'food-drinks', label: '🍕 Food & Drinks', description: 'Organizing refreshments, catering' },
  { value: 'event-planning', label: '📋 Event Planning', description: 'Organizing events, tournaments, meetings' },
  { value: 'coaching', label: '🏏 Coaching/Training', description: 'Helping with practice sessions' },
  { value: 'admin-work', label: '💼 Admin Work', description: 'Paperwork, registrations, communications' },
  { value: 'fundraising', label: '💰 Fundraising', description: 'Sponsorship, donations, events' },
  { value: 'photography', label: '📸 Photography/Media', description: 'Taking photos, social media' },
  { value: 'other', label: '📝 Other', description: 'Other volunteer activities' }
];

const VolunteerManagement = ({ clubId, players, games, events, isAdmin = true }) => {
  const [volunteerLogs, setVolunteerLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // overview, logs, add, leaderboard
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Form state for adding volunteer hours
  const [formData, setFormData] = useState({
    playerId: '',
    activity: '',
    description: '',
    hours: '',
    date: new Date().toISOString().split('T')[0],
    eventId: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Load volunteer logs
  useEffect(() => {
    if (clubId) {
      loadVolunteerLogs();
    }
  }, [clubId]);

  const loadVolunteerLogs = async () => {
    try {
      setLoading(true);
      const logsRef = collection(db, 'clubs', clubId, 'volunteerLogs');
      const q = query(logsRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);

      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVolunteerLogs(logs);
    } catch (error) {
      console.error('Error loading volunteer logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add volunteer hours
  const handleAddVolunteerHours = async (e) => {
    e.preventDefault();
    if (!formData.playerId || !formData.activity || !formData.hours || !formData.date) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const player = players.find(p => (p.userId || p.id) === formData.playerId);
      const event = formData.eventId ?
        [...(games || []), ...(events || [])].find(e => e.id === formData.eventId) : null;

      const logData = {
        playerId: formData.playerId,
        playerName: player?.name || 'Unknown',
        playerEmail: player?.email || '',
        activity: formData.activity,
        activityLabel: ACTIVITY_TYPES.find(a => a.value === formData.activity)?.label || formData.activity,
        description: formData.description,
        hours: parseFloat(formData.hours),
        date: formData.date,
        eventId: formData.eventId || null,
        eventName: event?.name || null,
        status: isAdmin ? 'approved' : 'pending', // Admin-added = auto-approved
        submittedBy: isAdmin ? 'admin' : formData.playerId,
        approvedBy: isAdmin ? 'admin' : null,
        approvedAt: isAdmin ? new Date().toISOString() : null,
        createdAt: new Date().toISOString(),
        notes: formData.notes
      };

      await addDoc(collection(db, 'clubs', clubId, 'volunteerLogs'), logData);

      // Reset form
      setFormData({
        playerId: '',
        activity: '',
        description: '',
        hours: '',
        date: new Date().toISOString().split('T')[0],
        eventId: '',
        notes: ''
      });
      setShowAddForm(false);

      // Reload logs
      await loadVolunteerLogs();

      alert('Volunteer hours added successfully!');
    } catch (error) {
      console.error('Error adding volunteer hours:', error);
      alert('Failed to add volunteer hours. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve/Reject volunteer hours
  const handleUpdateStatus = async (logId, newStatus, rejectReason = '') => {
    try {
      const logRef = doc(db, 'clubs', clubId, 'volunteerLogs', logId);
      await updateDoc(logRef, {
        status: newStatus,
        approvedBy: newStatus === 'approved' ? 'admin' : null,
        approvedAt: newStatus === 'approved' ? new Date().toISOString() : null,
        rejectedAt: newStatus === 'rejected' ? new Date().toISOString() : null,
        rejectReason: rejectReason || null
      });

      await loadVolunteerLogs();
    } catch (error) {
      console.error('Error updating volunteer log status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Delete volunteer log
  const handleDeleteLog = async (logId) => {
    if (!confirm('Are you sure you want to delete this volunteer log?')) return;

    try {
      await deleteDoc(doc(db, 'clubs', clubId, 'volunteerLogs', logId));
      await loadVolunteerLogs();
    } catch (error) {
      console.error('Error deleting volunteer log:', error);
      alert('Failed to delete. Please try again.');
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const approvedLogs = volunteerLogs.filter(l => l.status === 'approved');
    const totalHours = approvedLogs.reduce((sum, l) => sum + (l.hours || 0), 0);
    const uniqueVolunteers = new Set(approvedLogs.map(l => l.playerId)).size;
    const pendingCount = volunteerLogs.filter(l => l.status === 'pending').length;

    // This month
    const now = new Date();
    const thisMonth = approvedLogs.filter(l => {
      const logDate = new Date(l.date);
      return logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
    });
    const thisMonthHours = thisMonth.reduce((sum, l) => sum + (l.hours || 0), 0);

    return { totalHours, uniqueVolunteers, pendingCount, thisMonthHours };
  };

  // Calculate leaderboard
  const calculateLeaderboard = () => {
    const approvedLogs = volunteerLogs.filter(l => l.status === 'approved');
    const playerHours = {};

    approvedLogs.forEach(log => {
      if (!playerHours[log.playerId]) {
        playerHours[log.playerId] = {
          playerId: log.playerId,
          playerName: log.playerName,
          totalHours: 0,
          activities: {}
        };
      }
      playerHours[log.playerId].totalHours += log.hours || 0;
      playerHours[log.playerId].activities[log.activity] =
        (playerHours[log.playerId].activities[log.activity] || 0) + (log.hours || 0);
    });

    return Object.values(playerHours)
      .sort((a, b) => b.totalHours - a.totalHours);
  };

  // Filter logs
  const getFilteredLogs = () => {
    let filtered = [...volunteerLogs];

    if (filter !== 'all') {
      filtered = filtered.filter(l => l.status === filter);
    }

    if (selectedPlayer) {
      filtered = filtered.filter(l => l.playerId === selectedPlayer);
    }

    if (dateRange.start) {
      filtered = filtered.filter(l => l.date >= dateRange.start);
    }

    if (dateRange.end) {
      filtered = filtered.filter(l => l.date <= dateRange.end);
    }

    return filtered;
  };

  // Export to CSV
  const exportToCSV = () => {
    const logs = getFilteredLogs();
    const headers = ['Date', 'Player', 'Activity', 'Hours', 'Description', 'Event', 'Status', 'Notes'];
    const rows = logs.map(l => [
      l.date,
      l.playerName,
      l.activityLabel || l.activity,
      l.hours,
      l.description || '',
      l.eventName || '',
      l.status,
      l.notes || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteer-hours-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const stats = calculateStats();
  const leaderboard = calculateLeaderboard();
  const filteredLogs = getFilteredLogs();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Volunteer Tracking</h2>
          <p className="text-gray-600">Track and recognize volunteer contributions</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
        >
          <Plus size={20} />
          Add Volunteer Hours
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700">Total Hours</p>
              <p className="text-2xl font-bold text-green-900">{stats.totalHours.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-700">Volunteers</p>
              <p className="text-2xl font-bold text-blue-900">{stats.uniqueVolunteers}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-700">This Month</p>
              <p className="text-2xl font-bold text-purple-900">{stats.thisMonthHours.toFixed(1)}h</p>
            </div>
          </div>
        </div>

        {stats.pendingCount > 0 && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-700">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-900">{stats.pendingCount}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        <button
          onClick={() => setView('overview')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${view === 'overview'
            ? 'text-green-600 border-b-2 border-green-600'
            : 'text-gray-500 hover:text-gray-700'}`}
        >
          📋 All Logs
        </button>
        <button
          onClick={() => setView('leaderboard')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${view === 'leaderboard'
            ? 'text-green-600 border-b-2 border-green-600'
            : 'text-gray-500 hover:text-gray-700'}`}
        >
          🏆 Leaderboard
        </button>
        {stats.pendingCount > 0 && (
          <button
            onClick={() => { setView('overview'); setFilter('pending'); }}
            className={`px-4 py-2 font-medium whitespace-nowrap flex items-center gap-2 ${filter === 'pending'
              ? 'text-amber-600 border-b-2 border-amber-600'
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            ⏳ Pending
            <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{stats.pendingCount}</span>
          </button>
        )}
      </div>

      {/* Leaderboard View */}
      {view === 'leaderboard' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              Volunteer Leaderboard
            </h3>
          </div>

          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No volunteer hours recorded yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {leaderboard.map((player, index) => (
                <div key={player.playerId} className={`p-4 flex items-center gap-4 ${index < 3 ? 'bg-gradient-to-r from-amber-50 to-transparent' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-amber-600 text-white' :
                          'bg-gray-100 text-gray-600'
                    }`}>
                    {index < 3 ? ['🥇', '🥈', '🥉'][index] : index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{player.playerName}</p>
                    <p className="text-sm text-gray-500">
                      {Object.keys(player.activities).length} different activities
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-bold text-green-600">{player.totalHours.toFixed(1)}h</p>
                    <p className="text-xs text-gray-500">total hours</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Logs View */}
      {view === 'overview' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="all">All Status</option>
                  <option value="approved">✅ Approved</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="rejected">❌ Rejected</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Player</label>
                <select
                  value={selectedPlayer}
                  onChange={(e) => setSelectedPlayer(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">All Players</option>
                  {players.map(p => (
                    <option key={p.userId || p.id} value={p.userId || p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download size={18} />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {filteredLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No volunteer logs found</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="mt-4 text-green-600 hover:text-green-700 font-semibold"
                >
                  + Add first volunteer hours
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLogs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(log.date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{log.playerName}</div>
                          {log.eventName && (
                            <div className="text-xs text-gray-500">@ {log.eventName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{log.activityLabel || log.activity}</div>
                          {log.description && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">{log.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-green-600">{log.hours}h</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${log.status === 'approved' ? 'bg-green-100 text-green-800' :
                              log.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                            }`}>
                            {log.status === 'approved' ? '✅ Approved' :
                              log.status === 'pending' ? '⏳ Pending' : '❌ Rejected'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {log.status === 'pending' && isAdmin && (
                              <>
                                <button
                                  onClick={() => handleUpdateStatus(log.id, 'approved')}
                                  className="p-1 text-green-600 hover:bg-green-100 rounded"
                                  title="Approve"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = prompt('Reason for rejection (optional):');
                                    handleUpdateStatus(log.id, 'rejected', reason);
                                  }}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                  title="Reject"
                                >
                                  <X size={18} />
                                </button>
                              </>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteLog(log.id)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Volunteer Hours Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">Add Volunteer Hours</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddVolunteerHours} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Player <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.playerId}
                  onChange={(e) => setFormData(prev => ({ ...prev, playerId: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select Player</option>
                  {players.map(p => (
                    <option key={p.userId || p.id} value={p.userId || p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.activity}
                  onChange={(e) => setFormData(prev => ({ ...prev, activity: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select Activity</option>
                  {ACTIVITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                {formData.activity && (
                  <p className="text-xs text-gray-500 mt-1">
                    {ACTIVITY_TYPES.find(t => t.value === formData.activity)?.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hours <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={formData.hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 2.5"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Game/Event (optional)
                </label>
                <select
                  value={formData.eventId}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventId: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">No specific event</option>
                  <optgroup label="Games">
                    {(games || []).map(g => (
                      <option key={g.id} value={g.id}>🏏 {g.name} ({formatDate(g.date)})</option>
                    ))}
                  </optgroup>
                  <optgroup label="Events">
                    {(events || []).map(e => (
                      <option key={e.id} value={e.id}>🎉 {e.name} ({formatDate(e.date)})</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Brief description of the work done"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold"
                >
                  {submitting ? 'Adding...' : 'Add Hours'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerManagement;